import { DefaultFormsService } from '@app/services/default-forms.service';
import { ConfigServiceModel, FormType } from '@app/origami-api/services/config.service';
import { forkJoin, Observable } from 'rxjs';
import { StringHash } from '@app/shared/utils/utils-types';
import { map, take } from 'rxjs/operators';
import { ErrorCode, Field, TopicTypeId } from '@app/origami-api/constants';

export enum FieldsLevelEnum {
  Domain = 1,
  Room,
  Additional,
}

export class DefaultForms {
  constructor(
    private roomId: number,
    private systemRoomId: number,
    private defaultFormsService: DefaultFormsService,
  ) {}

  getForm(
    topicType: string | number,
    roomId?: number,
    isLinkedRoomMetadataMode?: boolean,
  ): Observable<ConfigServiceModel.Form> {
    if (!topicType) {
      return null;
    }

    if (typeof topicType !== 'number') {
      topicType = TopicTypeId[topicType];
    }
    return this.defaultFormsService.getFormById(roomId || this.roomId, <number>topicType, isLinkedRoomMetadataMode);
  }

  getForms(
    topicTypes: (string | number)[],
    roomId?: number,
    isLinkedRoomMetadataMode?: boolean,
  ): Observable<StringHash<ConfigServiceModel.Form>> {
    const topicTypeIds = topicTypes.map((topicType) =>
      typeof topicType === 'number' ? topicType : TopicTypeId[topicType],
    );

    return this.defaultFormsService.getFormsById(roomId || this.roomId, topicTypeIds, isLinkedRoomMetadataMode).pipe(
      map((hash) => {
        const res = {};

        topicTypes.forEach((topicType) => {
          const newIndex = typeof topicType === 'number' ? topicType : TopicTypeId[topicType];
          res[topicType] = hash[newIndex];
        });

        return res;
      }),
    );
  }

  getMergedForm(mainTopicType: number, roomId: number, systemRoomId: number): Observable<ConfigServiceModel.Form> {
    // TODO: add caching
    const topicTypes = [];
    switch (mainTopicType) {
      case TopicTypeId.Contact:
        topicTypes.push(TopicTypeId.User, TopicTypeId.User, TopicTypeId.Contact);
        break;
      case TopicTypeId.InvestigativeSite: // not used for now
        topicTypes.push(TopicTypeId.InvestigativeSite, TopicTypeId.InvestigativeSiteInDataRoom, null);
        break;
    }

    const obs = forkJoin([
      this.getForm(topicTypes[0], systemRoomId),
      this.getForm(topicTypes[1], roomId),
      this.getForm(topicTypes[2], roomId),
    ]).pipe(
      take(1),
      map(([domainForm, roomForm, additionalForm]) => {
        const fields = [];

        if (domainForm) {
          fields.push(
            ...domainForm.Fields.filter((field) => {
              return mainTopicType === TopicTypeId.Contact && Field.Is.includeInContact(field);
            }).map((field) => {
              field.Level = FieldsLevelEnum.Domain;

              return field;
            }),
          );
        }

        if (roomForm) {
          fields.push(
            ...roomForm.Fields.filter((field) => {
              return mainTopicType === TopicTypeId.Contact && Field.Is.includeInContact(field);
            }).map((field) => {
              field.SortOrder = field.SortOrder * 100;
              field.Level = FieldsLevelEnum.Room;

              return field;
            }),
          );
        }

        if (additionalForm) {
          fields.push(
            ...additionalForm.Fields.map((field) => {
              field.SortOrder = field.SortOrder * 10000;
              field.Level = FieldsLevelEnum.Additional;

              return field;
            }),
          );
        }

        const mergedForm: ConfigServiceModel.Form = {
          FormId: null,
          FormName: null,
          FormTypeId: null,
          SiteId: this.roomId,
          TopicTypeId: mainTopicType,
          Fields: fields,
        };

        return mergedForm;
      }),
    );
    return obs;
  }

  getCustomFieldsForm(
    formId: number,
    formType: FormType,
    topicTypeId: number,
    roomId?: number,
    options?,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<ConfigServiceModel.Form> {
    //todo: add for signature next pattern - options: {formId: number, formType: FormType, ...}
    return this.defaultFormsService.getCustomFieldsForm(
      formId,
      formType,
      roomId || this.roomId,
      topicTypeId,
      options,
      silentErrorCodes,
      forceRequest,
    );
  }
}
